import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Button from 'components/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import { zIndexValues } from 'utils/styles';

import IssueDetailsLoader from 'views/IssueDetails/components/Loaders/IssueDetailsLoader';
import AILoader from 'views/IssueDetails/components/Loaders/AILoader';
import Info from 'views/IssueDetails/components/Info';
import AttachmentSections from 'views/IssueDetails/components/AIAnalysis/components/AttachmentSections';
import RightSections from 'views/IssueDetails/components/AIAnalysis/components/RightSections';
import OverallSummary from 'views/IssueDetails/components/AIAnalysis/components/OverallSummary';
import SectionHeader from 'views/IssueDetails/components/AIAnalysis/components/SectionHeader';
import {
  IssueDetailsContainer,
  Left,
  Right,
} from 'views/IssueDetails/components/AIAnalysis/Styles';
import { useAIAnalytics } from 'views/IssueDetails/components/AIAnalysis/hooks/useAIAnalytics';
import { useHighlighting } from 'views/IssueDetails/components/AIAnalysis/hooks/useHighlighting';

/**
 * NIDC Checkup layout for AI file descriptions
 * This layout handles the standard NIDC checkup workflow with left/right panels
 */
const NIDCCheckupLayout = ({
  analyticData,
  fetchData,
  fetchStatus,
  issue,
  rerunDataAnalysis,
}) => {
  const { attachments, isLoading, isInProgress } = useAIAnalytics({
    analyticData,
    fetchData,
    fetchStatus,
    rerunDataAnalysis,
  });

  const highlighting = useHighlighting();

  // If loading or in progress, show loading state
  if (isLoading || isInProgress) {
    return (
      <Fragment>
        <IssueDetailsContainer container>
          <Grid
            size={12}
            style={{ position: 'relative', marginTop: 20, marginBottom: 20 }}>
            <AILoader />
            <IssueDetailsLoader />
            <IssueDetailsLoader />
          </Grid>
        </IssueDetailsContainer>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <IssueDetailsContainer container>
        <Left size={8}>
          <Info issue={issue} />
          <Divider />

          {/* Left Header Section */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              padding: '0 16px',
            }}>
            <SectionHeader style={{ marginBottom: 0 }}>
              判定結果 / コメント
            </SectionHeader>
            <Button
              variant="primary"
              onClick={rerunDataAnalysis}
              icon={<RefreshIcon />}
              disabled={isInProgress}
              style={{ marginLeft: '16px' }}>
              Rerun Data Analysis
            </Button>
          </div>

          {/* Left Content */}
          <AttachmentSections
            attachments={attachments}
            onItemClick={highlighting.highlightItemInRightPanel}
          />
          <SectionHeader>Overall Summary</SectionHeader>
          <OverallSummary analyticData={analyticData} />
        </Left>

        <Right size={4}>
          <Stack>
            {/* Right Header Section */}
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: zIndexValues.modal4 + 1,
                backgroundColor: '#FFF',
                paddingBottom: '8px',
              }}>
              <SectionHeader>AI分析・洞察</SectionHeader>
            </div>

            {/* Right Content */}
            <RightSections
              attachments={attachments}
              onItemClick={highlighting.highlightItemInLeftPanel}
            />
          </Stack>
        </Right>
      </IssueDetailsContainer>
    </Fragment>
  );
};

NIDCCheckupLayout.propTypes = {
  analyticData: PropTypes.object.isRequired,
  fetchData: PropTypes.func.isRequired,
  issue: PropTypes.object.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  rerunDataAnalysis: PropTypes.func.isRequired,
};

export default NIDCCheckupLayout;
